import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { authAPI, api } from '@/lib/api';
import { Sparkles, MessageSquare, Send, Home } from 'lucide-react';
import { toast } from 'sonner';

const Comments = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingComments, setLoadingComments] = useState(true);

  useEffect(() => {
    loadUser();
    loadComments();
  }, []);

  const loadUser = async () => {
    try {
      const userData = await authAPI.getMe();
      setUser(userData);
    } catch (error) {
      console.error('Load user error:', error);
    }
  };

  const loadComments = async () => {
    try {
      setLoadingComments(true);
      const response = await api.get('/comments');
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Load comments error:', error);
      toast.error('Failed to load comments');
    } finally {
      setLoadingComments(false);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim() || newComment.trim().length < 3) {
      toast.error('Comment must be at least 3 characters');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/comments', {
        content: newComment.trim()
      });
      
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success('Comment posted successfully!');
    } catch (error) {
      console.error('Submit comment error:', error);
      toast.error(error.response?.data?.detail || 'Failed to post comment');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen" data-testid="comments-page">
      <nav className="px-6 py-4 bg-white/95 backdrop-blur-sm border-b border-border shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
            <Sparkles className="h-7 w-7 text-primary" />
            <h1 className="text-xl font-semibold text-foreground">TerryLBPrompts</h1>
          </div>
          {user && (
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="hidden md:flex items-center gap-2"
              >
                <Home className="h-4 w-4" />
                Dashboard
              </Button>
              <div className="bg-secondary px-4 py-2 rounded-lg border border-border">
                <span className="font-semibold text-sm text-foreground">Level {user.level} • {user.points} pts</span>
              </div>
              <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full border-2 border-border" />
            </div>
          )}
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-3">
            <MessageSquare className="h-8 w-8 text-primary" />
            <h2 className="text-4xl md:text-5xl font-bold text-foreground">Community Feedback</h2>
          </div>
          <p className="text-xl text-muted-foreground">Share your thoughts and see what others are saying about TerryLBPrompts</p>
        </div>

        {/* Comment Form */}
        <Card className="p-6 bg-white border border-border card-shadow-lg mb-8">
          <form onSubmit={handleSubmitComment}>
            <div className="flex items-start gap-4">
              {user && (
                <img src={user.picture} alt={user.name} className="w-12 h-12 rounded-full border-2 border-border flex-shrink-0" />
              )}
              <div className="flex-1">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Share your experience with TerryLBPrompts..."
                  className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  rows="4"
                  disabled={loading}
                />
                <div className="flex justify-between items-center mt-3">
                  <span className="text-sm text-muted-foreground">
                    {newComment.length}/500 characters
                  </span>
                  <Button
                    type="submit"
                    disabled={loading || !newComment.trim() || newComment.length > 500}
                    className="bg-primary text-white rounded-lg shadow-md hover:shadow-lg font-semibold flex items-center gap-2"
                  >
                    <Send className="h-4 w-4" />
                    {loading ? 'Posting...' : 'Post Comment'}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </Card>

        {/* Comments List */}
        <div className="space-y-4">
          {loadingComments ? (
            <Card className="p-6 bg-white border border-border">
              <p className="text-center text-muted-foreground">Loading comments...</p>
            </Card>
          ) : comments.length === 0 ? (
            <Card className="p-8 bg-white border border-border text-center">
              <MessageSquare className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-lg text-muted-foreground">No comments yet. Be the first to share your thoughts!</p>
            </Card>
          ) : (
            comments.map((comment) => (
              <Card key={comment.comment_id} className="p-6 bg-white border border-border card-shadow hover:shadow-lg transition-all">
                <div className="flex items-start gap-4">
                  <img 
                    src={comment.user_picture || 'https://api.dicebear.com/7.x/avataaars/svg?seed=default'} 
                    alt={comment.user_name} 
                    className="w-12 h-12 rounded-full border-2 border-border flex-shrink-0" 
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-semibold text-foreground">{comment.user_name}</h4>
                      <span className="text-sm text-muted-foreground">•</span>
                      <span className="text-sm text-muted-foreground">{formatDate(comment.created_at)}</span>
                    </div>
                    <p className="text-foreground leading-relaxed">{comment.content}</p>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-20 py-8 border-t border-border bg-white/50">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-muted-foreground">
            Need help? Contact us at{' '}
            <a href="mailto:computerwork2022@gmail.com" className="text-primary hover:underline font-medium">
              computerwork2022@gmail.com
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Comments;
